import React, { useEffect } from 'react';
import { compose, map, sort } from 'ramda';
import { reduxForm, InjectedFormProps, Form } from 'redux-form';
import Button from '@material-ui/core/Button';
import FormHelperText from '@material-ui/core/FormHelperText';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import WizardCard from 'components/WizardCard/WizardCard';
import Fields from 'components/Fields';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import IntlProvider, {
  useFormatMessage,
  IntlProviderType,
  getLabelByIntlPrefix,
} from 'components/IntlProvider';

import { validate, wizardStyles } from './wizardUtils';

const { connect, selectors, actions } = StoreProvider;

interface CategoryType {
  id: number;
  category: string;
}

interface ProductGroupProps {
  categories: CategoryType[];
  clearSubcategories: () => void;
  intl: IntlProviderType;
}

const ProductGroup = ({
  handleSubmit,
  error,
  categories,
  clearSubcategories,
}: ProductGroupProps & InjectedFormProps) => {
  const classes = wizardStyles();
  const t = useFormatMessage();
  useEffect(() => {
    clearSubcategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <WizardCard>
      <Form onSubmit={handleSubmit} className={classes.form} noValidate>
        <Grid item xs={12}>
          <Typography
            component="h1"
            variant="h5"
            color="inherit"
            className={classes.title}
          >
            {t(
              'forms.wizard.productgroup',
              'Give us some information about your product'
            )}
          </Typography>
          <Fields.Wizard.Category categories={categories} />
          {error && <FormHelperText error>{error}</FormHelperText>}
          <Button
            classes={{
              root: classes.submitButton,
            }}
            type="submit"
            variant="contained"
            color="primary"
          >
            {t('fields.wizard.common.continue', 'Continue')}
          </Button>
        </Grid>
      </Form>
    </WizardCard>
  );
};

export default compose(
  IntlProvider.withIntl,
  connect(
    (state, props: ProductGroupProps) => ({
      categories: compose(
        map((category: CategoryType) => ({
          ...category,
          value: category.id.toString(),
          label: getLabelByIntlPrefix(category, props.intl.locale, 'category'),
        })),
        sort((a: CategoryType, b: CategoryType) => a.id - b.id)
      )(selectors.assistantSelectors.categories(state)),
    }),
    {
      clearSubcategories:
        actions.assistantActions.clearAssistantCategorySubcategories,
    }
  ),
  reduxForm({
    form: 'wizard',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
    validate,
  })
)(ProductGroup);
